// apps/admin/src/features/billing/PaymentRequestPdfModal.tsx
//
// Phase 8.2 Wave 4 Task 26 — Pre-payment Request PDF preview modal.
//
// vg-binding: CONTEXT:decisions=P8.D-236,P8.D-238,P8.D-239,P8.D-243
// vg-binding: PHASE:08.2-admin-finance-catalog
//
//  Actions: "Preview PDF trong modal + Download". No edit/cancel
//  (immutable). PDF is English-only + lives on dedicated
// server storage. The modal opens with the absolute / signed URL returned
// by GET /api/v1/admin/payment-requests/:id (which mirrors invoice PDF
// shape — `pdf_url` nullable while async generation in flight).
//
// Test IDs (per task-26 spec):
//   payment-request-pdf-modal, payment-request-pdf-download-btn

import type { JSX } from 'react';

import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

export interface PaymentRequestPdfModalProps {
  /** PR row identity for header + accessible iframe title. */
  prNumber: string | null;
  /** Absolute / signed URL returned by BE. null while async render. */
  pdfUrl: string | null | undefined;
  open: boolean;
  onClose: () => void;
}

// Phase 8.2 debug #15 — BE returns relative URLs like
// `/api/v1/admin/payment-requests/{id}/pdf`. Without prefixing, the iframe
// resolves against the admin-v3 origin (which is a static SPA + has NO
// /api/v1 proxy rule), so the request lands on the admin index.html and
// the React Router catch-all renders the SPA 404 page inside the iframe.
// Prefix with VITE_API_BASE_URL to point the iframe at the actual API host.
function resolvePdfUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined)
    ?.replace(/\/+$/, '');
  if (!base) return raw;
  return `${base}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

/**
 * Renders a Shadcn-style Dialog with the PR PDF inside an `<iframe>` and a
 * Download button that triggers the browser's native save dialog via the
 * underlying URL. When `pdfUrl` is null (BE still rendering) the modal
 * surfaces an empty-state placeholder instead — matching InvoicePdfPreview.
 */
export function PaymentRequestPdfModal({
  prNumber,
  pdfUrl,
  open,
  onClose,
}: PaymentRequestPdfModalProps): JSX.Element | null {
  const { t } = useTranslation('finance');
  if (!open) return null;
  const resolvedUrl = resolvePdfUrl(pdfUrl);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      className="max-w-4xl"
      data-testid="payment-request-pdf-modal"
    >
      <DialogHeader>
        <DialogTitle>
          {t('payment_request.pdf.title')}
          {prNumber ? ` · ${prNumber}` : ''}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 px-6 pb-6">
        {resolvedUrl ? (
          <iframe
            src={resolvedUrl}
            title={t('payment_request.pdf.iframeTitle', { prNumber: prNumber ?? '' })}
            className="h-[70vh] w-full rounded-md border border-pw-border bg-white" /* dark-ok: PDF iframe renders a white paper document */
            data-testid="payment-request-pdf-iframe"
          />
        ) : (
          <div
            className="flex h-[40vh] flex-col items-center justify-center gap-2 rounded-md border border-dashed border-pw-border bg-pw-surface/40 px-6 py-12 text-center text-sm text-pw-text-muted"
            data-testid="payment-request-pdf-empty"
          >
            <span aria-hidden="true" className="text-2xl">▢</span>
            <p>{t('payment_request.pdf.notReady')}</p>
            <p className="text-xs">
              {t('payment_request.pdf.notReadyHint')}
            </p>
          </div>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('payment_request.pdf.close')}
          </Button>
          {resolvedUrl ? (
            <a
              href={resolvedUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              data-testid="payment-request-pdf-download-btn"
              className="inline-flex h-9 items-center justify-center rounded-md bg-pw-primary px-4 text-sm font-medium text-white transition-colors hover:bg-pw-primary-hover focus:outline-none focus:ring-2 focus:ring-pw-primary/30"
            >
              {t('payment_request.pdf.download')}
            </a>
          ) : (
            <Button variant="default" disabled data-testid="payment-request-pdf-download-btn">
              {t('payment_request.pdf.download')}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export default PaymentRequestPdfModal;
